/**
 * Pure-ish state-machine driver for the Settings page's "bind real
 * Firebase user to assignee" picker.
 *
 * Why this exists as a standalone helper instead of inlined in the
 * popover: the previous native `<select>` flow had the dropdown vanish
 * the instant the user clicked an option (browser default), so the user
 * had no way to tell whether the save was in flight, succeeded, or
 * failed. The new popover keeps itself open across the await — and to
 * keep that ordering testable without jsdom we extract the sequence
 * here. The popover composes this with React state setters; tests
 * compose it with jest-style spies.
 *
 * Contract:
 *   - `onStart` runs *before* the API call so the spinner shows even
 *     when the API resolves synchronously.
 *   - `onSuccess` runs only on `{ ok: true }` — popover closes and the
 *     onSnapshot listener takes over (no optimistic update needed).
 *   - `onError` runs on `{ ok: false }` *or* a thrown exception — the
 *     popover stays open and surfaces the message inline. The list
 *     visibly rolls back because we never optimistically committed.
 */

export type UserMappingPickApiResult =
  | { ok: true }
  | { ok: false; error: string };

export interface UserMappingPickEvents {
  onStart: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
}

export async function runUserMappingPick(
  api: () => Promise<UserMappingPickApiResult>,
  events: UserMappingPickEvents,
): Promise<void> {
  events.onStart();
  let result: UserMappingPickApiResult;
  try {
    result = await api();
  } catch (e) {
    events.onError(e instanceof Error ? e.message : '紐付けに失敗しました');
    return;
  }
  if (result.ok) {
    events.onSuccess();
  } else {
    events.onError(result.error);
  }
}
